import apiClient from "./apiClient";
import { AxiosError } from "axios";
import serviceConfig from "config/serviceConfig";

import getClientDeviceInfo from "@utils/getClientDeviceInfo";
import { setCookie } from "@utils/Cookie";
import i18n from "locales/i18n";

/**
 * Login Service
 */
export const login = async (username: string, password: string) => {
	const basicAuth = btoa(`${username}:${password}`);
	const deviceInfo = getClientDeviceInfo();

	try {
		const response = await apiClient.post(
			serviceConfig.routes.login,
			{},
			{
				headers: {
					CredType: "email",
					// This is the analytics/system panel: authenticate as a system
					// user so GuardAdmin grants the system role (see admin backend).
					"User-Type": "system",
					Authorization: `Basic ${basicAuth}`,
					"X-Device-Name": deviceInfo.deviceName,
					"X-Device-Model": deviceInfo.deviceModel,
					"X-Device-Firmware": deviceInfo.deviceFirmware,
					"X-App-Version": deviceInfo.appVersion,
					"X-App-Name": deviceInfo.appName,
				},
			},
		);
		return response.data;
	} catch (error) {
		if (error instanceof AxiosError) {
			throw new Error(
				error.response?.data?.message || i18n.t("errors.loginFailed"),
			);
		}
		throw new Error(i18n.t("errors.unknownLogin"));
	}
};

/**
 * Refresh Token Service
 */
export const refreshToken = async () => {
	try {
		const refreshToken = localStorage.getItem("refresh_token");
		if (!refreshToken) throw new Error("No refresh token available");

		const response = await apiClient.post(serviceConfig.routes.refreshToken, {
			refresh_token: refreshToken,
		});
		return response.data.access_token;
	} catch (error) {
		if (error instanceof AxiosError) {
			throw new Error(
				error.response?.data?.message || "Failed to refresh token",
			);
		}
		throw new Error("An unknown error occurred during token refresh");
	}
};

/**
 * Register Request Service
 */
export const registerRequest = async (email: string, roleID: number) => {
	try {
		const response = await apiClient.post(
			serviceConfig.routes.registerRequest,
			{},
			{
				headers: {
					Credentials: email,
					CredType: "email",
					RoleID: roleID,
				},
			},
		);

		const setCookieHeader = response.headers["set-cookie"];
		if (Array.isArray(setCookieHeader)) {
			const sessionCookie = setCookieHeader.find((cookie) =>
				cookie.startsWith("texsession="),
			);
			if (sessionCookie) {
				const cookieValue = sessionCookie.split(";")[0].split("=")[1];
				setCookie("texsession", cookieValue, 5);
			}
		}

		return response.data;
	} catch (error) {
		if (error instanceof AxiosError) {
			throw new Error(
				error.response?.data?.message || "Failed to send register request",
			);
		}
		throw new Error("An unknown error occurred during register request");
	}
};

/**
 * Validate OTP Service
 */
export const validateOtp = async (otp: string, email: string) => {
	try {
		const response = await apiClient.post(
			serviceConfig.routes.validateOtp,
			{},
			{
				headers: {
					OTP: otp,
					CredType: "email",
					Credentials: email,
				},
			},
		);
		return response.data;
	} catch (error) {
		if (error instanceof AxiosError) {
			throw new Error(
				error.response?.data?.message || "Failed to validate OTP",
			);
		}
		throw new Error("An unknown error occurred during OTP validation");
	}
};

/**
 * Register User Service
 */
export const register = async ({
	email,
	password,
	roleID,
	otp,
}: {
	email: string;
	password: string;
	roleID: number;
	otp: string;
}) => {
	try {
		const response = await apiClient.post(
			serviceConfig.routes.register,
			{
				email,
				password,
				role_id: roleID,
				username: "",
			},
			{
				headers: {
					CredType: "email",
					Credentials: email,
					OTP: otp,
				},
			},
		);
		return response.data;
	} catch (error) {
		if (error instanceof AxiosError) {
			throw new Error(error.response?.data?.message || "Failed to register");
		}
		throw new Error("An unknown error occurred during registration");
	}
};

export const logout = async () => {
	const accessToken = localStorage.getItem("access_token");
	const refreshToken = localStorage.getItem("refresh_token");

	await apiClient.post(
		serviceConfig.routes.logout,
		{ refresh_token: refreshToken },
		{
			headers: {
				Authorization: `Bearer ${accessToken}`,
				"Content-Type": "application/json",
			},
		},
	);
};
